import Alert from "components/common/Alert";
import { useEffect, useState, useRef, useMemo } from "react";
import { useOrganizers } from "components/company/hooks/useOrganizers";
import { usePavilions } from "components/pavilions/hooks/usePavilions";
import { useEventCategory } from "components/eventCategory/hooks/useEventCategory";
import { getExhibitor } from "api/exhibitor.api";
import { getImageUrl } from "utils/imageHelper";
import { MdDelete } from "react-icons/md";

export default function EventsForm({
  initialData = null,
  onSubmit,
  submitLabel,
  onBack,
}) {
  const { companies } = useOrganizers();
  const { loadPavilions, data: pavilions } = usePavilions();
  const { categories, loadCategoriesByOrganizer } = useEventCategory();
  const [errors, setErrors] = useState({});
  const [exhibitors, setExhibitors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState("");
  const alertRef = useRef(null);

  const [formData, setFormData] = useState({
    company_id: "",
    event_category_id: "",
    event_name: "",
    event_dates: [{ from_date: "", to_date: "" }],
    description: "",
    location: "",
    eventImage: [],
    eventVideo: [],
    default_media: "",
    exhibitors: [],
    status: "active",
  });

  const normalizePath = (path) => {
    if (!path || typeof path !== "string") return "";
    let cleaned = path
      .replace(/\\/g, "/")
      .replace(/\/{2,}/g, "/")
      .trim();
    cleaned = cleaned.replace(":/", "://");
    return cleaned;
  };

  const formatInputDateTimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const defaultMeta = useMemo(() => {
    if (!formData.default_media) return null;
    if (typeof formData.default_media === "object") return formData.default_media;
    try {
      return JSON.parse(formData.default_media);
    } catch {
      return null;
    }
  }, [formData.default_media]);

  const isDefaultMedia = (item, index, mediaType) => {
    if (!defaultMeta) return false;
    const isExisting = typeof item === "string";

    if (defaultMeta.type === "image" || defaultMeta.type === "video") {
      return (
        defaultMeta.type === mediaType &&
        isExisting &&
        normalizePath(defaultMeta.value) === normalizePath(item)
      );
    }

    if (defaultMeta.mediaType === mediaType) {
      if (defaultMeta.type === "existing" && isExisting) {
        return normalizePath(defaultMeta.value) === normalizePath(item);
      }
      if (defaultMeta.type === "new" && !isExisting) {
        return defaultMeta.value === index;
      }
    }

    return false;
  };

  const getAvailableExhibitors = (currentIndex) => {
    const selectedIds = formData.exhibitors
      .map((e, i) => (i !== currentIndex ? e.exhibitor_id : null))
      .filter(Boolean);
    return exhibitors.filter((e) => !selectedIds.includes(e._id));
  };

  useEffect(() => {
    if (!initialData?.event) return;

    setFormData({
      company_id: initialData.event.company_id?._id || "",
      event_category_id: initialData.event.event_category_id?._id || "",
      event_name: initialData.event.event_name || "",
      event_dates: (initialData.event.event_dates || []).map((d) => ({
        from_date: formatInputDateTimeLocal(d.from_date),
        to_date: formatInputDateTimeLocal(d.to_date),
      })),
      description: initialData.event.description || "",
      location: initialData.event.location || "",
      eventImage: initialData.event.eventImage || [],
      eventVideo: initialData.event.eventVideo || [],
      default_media: initialData.event.default_media || "",
      status: initialData.event.status || "active",
      exhibitors: (initialData.exhibitors || []).map((e) => ({
        exhibitor_id: e.exhibitor_id?._id,
        pavilion_id: e.pavilion_id?._id,
        stall_no: e.stall_no || "",
        floor_no: e.floor_no || "",
      })),
    });
  }, [initialData]);

  useEffect(() => {
    const companyId = localStorage.getItem("company_id");
    getExhibitor(companyId)
      .then((res) => setExhibitors(res.data || []))
      .catch(() => setExhibitors([]));
    loadPavilions(companyId);
    loadCategoriesByOrganizer(companyId);
  }, []);

  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [alert]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleExhibitorChange = (index, key, value) => {
    const copy = [...formData.exhibitors];
    copy[index][key] = value;
    setFormData((p) => ({ ...p, exhibitors: copy }));
  };

  const handleMultimedia = (e) => {
    const files = Array.from(e.target.files);
    const images = [];
    const videos = [];
    files.forEach((file) => {
      if (file.type.startsWith("image/")) images.push(file);
      else if (file.type.startsWith("video/")) videos.push(file);
    });
    setFormData((prev) => ({
      ...prev,
      eventImage: [...prev.eventImage, ...images],
      eventVideo: [...prev.eventVideo, ...videos],
    }));
  };

  const addDateSlot = () => {
    setFormData((p) => ({
      ...p,
      event_dates: [...p.event_dates, { from_date: "", to_date: "" }],
    }));
  };

  const removeDateSlot = (index) => {
    setFormData((p) => ({
      ...p,
      event_dates: p.event_dates.filter((_, i) => i !== index),
    }));
  };

  const handleDateChange = (index, key, value) => {
    const updated = [...formData.event_dates];
    updated[index][key] = value;
    setFormData((p) => ({ ...p, event_dates: updated }));
  };

  const removeImage = (index) => {
    setFormData((p) => {
      const updated = [...p.eventImage];
      const removed = updated.splice(index, 1)[0];

      let newDefault = p.default_media;

      try {
        const parsed =
          typeof p.default_media === "object"
            ? p.default_media
            : JSON.parse(p.default_media || "{}");

        const removedPath = normalizePath(removed);
        const isBackendDefault =
          parsed.type === "image" &&
          normalizePath(parsed.value) === removedPath;

        const isFrontendDefault =
          parsed.mediaType === "image" &&
          ((parsed.type === "existing" && normalizePath(parsed.value) === removedPath) ||
            (parsed.type === "new" && parsed.value === index));

        if (isBackendDefault || isFrontendDefault) newDefault = "";
      } catch {}

      return { ...p, eventImage: updated, default_media: newDefault };
    });
  };

  const removeVideo = (index) => {
    setFormData((p) => {
      const updated = [...p.eventVideo];
      updated.splice(index, 1);
      return { ...p, eventVideo: updated };
    });
  };

  const setDefaultMedia = (config) => {
    setFormData((prev) => ({
      ...prev,
      default_media: JSON.stringify({
        type: config.type,          
        mediaType: config.mediaType, 
        value: config.value,         
      }),
    }));
  };

  const addExhibitor = () => {
    setFormData((p) => ({
      ...p,
      exhibitors: [
        ...p.exhibitors,
        { exhibitor_id: "", pavilion_id: "", stall_no: "", floor_no: "" },
      ],
    }));
  };

  const removeExhibitor = (index) => {
    setFormData((p) => ({
      ...p,
      exhibitors: p.exhibitors.filter((_, i) => i !== index),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!formData.event_category_id) newErrors.event_category_id = "Event category is required";
    if (!formData.event_name?.trim()) newErrors.event_name = "Event name is required";
    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.description) newErrors.description = "Description is required";

    if (!formData.event_dates || formData.event_dates.length === 0) {
      newErrors.date = "At least one event date is required";
    } else {
      let hasValidSlot = false;
      formData.event_dates.forEach((slot) => {
        if (slot.from_date && slot.to_date) {
          hasValidSlot = true;
          if (new Date(slot.from_date) > new Date(slot.to_date)) {
            newErrors.date = "Start date cannot be greater than End date";
          }
        }
      });
      if (!hasValidSlot) newErrors.date = "At least one start and end date must be filled";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setAlert("Please fix the errors below");
      return;
    }

    setErrors({});

    const fd = new FormData();

    const existingImages = formData.eventImage.filter((img) => typeof img === "string");
    const existingVideos = formData.eventVideo.filter((vid) => typeof vid === "string");

    fd.append("existing_eventImage", JSON.stringify(existingImages));
    fd.append("existing_eventVideo", JSON.stringify(existingVideos));

    formData.eventImage.forEach((img) => {
      if (img instanceof File) fd.append("eventImage", img);
    });
    formData.eventVideo.forEach((vid) => {
      if (vid instanceof File) fd.append("eventVideo", vid);
    });

    if (formData.default_media) {
      fd.append(
        "default_media",
        typeof formData.default_media === "object"
          ? JSON.stringify(formData.default_media)
          : formData.default_media
      );
    }

    fd.append("event_name", formData.event_name);
    fd.append("location", formData.location);
    fd.append("description", formData.description);
    fd.append("status", formData.status);
    fd.append("event_category_id", formData.event_category_id);

    const formattedDates = formData.event_dates
      .filter((d) => d.from_date && d.to_date)
      .map((d) => ({
        from_date: new Date(d.from_date).toISOString(),
        to_date: new Date(d.to_date).toISOString(),
      }));
    fd.append("event_dates", JSON.stringify(formattedDates));

    const cleanedExhibitors = formData.exhibitors
      .filter((ex) => ex.exhibitor_id && ex.pavilion_id)
      .map((ex) => ({
        exhibitor_id: ex.exhibitor_id,
        pavilion_id: ex.pavilion_id,
        stall_no: ex.stall_no,
        floor_no: ex.floor_no,
      }));
    fd.append("exhibitors", JSON.stringify(cleanedExhibitors));

    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-10">
      {alert && (
        <div ref={alertRef} className="scroll-mt-[100px]">
          <Alert type="error" message={alert} onClose={() => setAlert("")} />
        </div>
      )}

      <Section title="Event Details">
        <SelectInput
          label="Event Category"
          value={formData.event_category_id}
          required
          error={errors.event_category_id}
          onChange={(e) => setFormData((p) => ({ ...p, event_category_id: e.target.value }))}
        >
          <option value="">Select Category</option>
          {categories.map((cat) => (
            <option key={cat._id} value={cat._id}>{cat.title}</option>
          ))}
        </SelectInput>

        <Input
          label="Event Name"
          name="event_name"
          value={formData.event_name}
          onChange={handleChange}
          required
          error={errors.event_name}
        />

        <Input
          label="Location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          required
          error={errors.location}
        />

        <div className="md:col-span-2 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Event Dates</h3>
            <button
              type="button"
              onClick={addDateSlot}
              className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition"
            >
              + Add Days
            </button>
          </div>

          <div className="relative bg-white border rounded-2xl p-5 shadow-sm">
            {formData.event_dates.map((slot, index) => (
              <div
                key={index}
                className="relative bg-gray-50 border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition mb-6"
              >
                <div className="absolute -top-3 left-4 bg-brand-500 text-white text-xs px-3 py-1 rounded-full shadow">
                  Day {index + 1}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                  <Input
                    type="datetime-local"
                    label="Start Date"
                    value={slot.from_date}
                    required
                    onChange={(e) => handleDateChange(index, "from_date", e.target.value)}
                  />
                  <Input
                    type="datetime-local"
                    label="End Date"
                    value={slot.to_date}
                    required
                    onChange={(e) => handleDateChange(index, "to_date", e.target.value)}
                  />
                  <div className="flex justify-center md:justify-start">
                    <button
                      type="button"
                      onClick={() => removeDateSlot(index)}
                      className="h-10 w-10 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {errors.date && <p className="text-red-500 text-sm mt-2">{errors.date}</p>}
        </div>

        <SelectInput
          label="Event Status"
          value={formData.status}
          onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </SelectInput>

        <Textarea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          error={errors.description}
        />
      </Section>

      <Section title="Event Media">
        <div className="md:col-span-2">
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleMultimedia}
            className="w-full rounded-xl border p-3"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">

            {formData.eventImage.map((img, i) => {
              const isExisting = typeof img === "string";
              const src = isExisting ? getImageUrl(img) : URL.createObjectURL(img);
              const isDefault = isDefaultMedia(img, i, "image");

              return (
                <div
                  key={`img-${i}`}
                  className={`relative border rounded-xl p-2 transition ${
                    isDefault ? "ring-2 ring-brand-500 shadow-lg scale-[1.02]" : "hover:shadow-md"
                  }`}
                >
                  <img src={src} alt="" className="h-32 w-full object-cover rounded-lg" />
                  <div className="mt-2 flex justify-between">
                    {isDefault ? (
                      <span className="text-xs font-medium text-green-600">Default Image</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setDefaultMedia({
                            type: isExisting ? "existing" : "new",
                            mediaType: "image",
                            value: isExisting ? img : i,
                          })
                        }
                        className="text-xs text-blue-600"
                      >
                        Set Default
                      </button>
                    )}
                    <button type="button" onClick={() => removeImage(i)} className="text-xs text-red-600">
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}

            {formData.eventVideo.map((vid, i) => {
              const isExisting = typeof vid === "string";
              const src = isExisting ? getImageUrl(vid) : URL.createObjectURL(vid);
              const isDefault = isDefaultMedia(vid, i, "video");

              return (
                <div
                  key={`vid-${i}`}
                  className={`relative border rounded-xl p-2 transition ${
                    isDefault ? "ring-2 ring-brand-500 shadow-lg scale-[1.02]" : "hover:shadow-md"
                  }`}
                >
                  <video src={src} controls className="h-32 w-full object-cover rounded-lg" />
                  <div className="mt-2 flex justify-between">
                    {isDefault ? (
                      <span className="text-xs font-medium text-green-600">Default Video</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setDefaultMedia({
                            type: isExisting ? "existing" : "new",
                            mediaType: "video",
                            value: isExisting ? vid : i,
                          })
                        }
                        className="text-xs text-blue-600"
                      >
                        Set Default
                      </button>
                    )}
                    <button type="button" onClick={() => removeVideo(i)} className="text-xs text-red-600">
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section title="Exhibitors">
        <div className="col-span-2 space-y-3">
          {formData.exhibitors.map((ex, i) => (
            <div key={i} className="flex gap-2 items-end">
              <select
                className="w-full rounded-xl border p-3"
                value={ex.exhibitor_id}
                onChange={(e) => handleExhibitorChange(i, "exhibitor_id", e.target.value)}
              >
                <option value="">Exhibitor</option>
                {getAvailableExhibitors(i).map((e) => (
                  <option key={e._id} value={e._id}>{e.title}</option>
                ))}
              </select>

              <select
                className="w-full rounded-xl border p-3"
                value={ex.pavilion_id}
                onChange={(e) => handleExhibitorChange(i, "pavilion_id", e.target.value)}
              >
                <option value="">Pavilion</option>
                {pavilions.map((p) => (
                  <option key={p._id} value={p._id}>{p.pavilion_name}</option>
                ))}
              </select>

              <input
                className="w-28 rounded-xl border p-3"
                placeholder="Stall"
                value={ex.stall_no}
                onChange={(e) => handleExhibitorChange(i, "stall_no", e.target.value)}
              />

              <input
                className="w-28 rounded-xl border p-3"
                placeholder="Floor No."
                value={ex.floor_no || ""}
                onChange={(e) => handleExhibitorChange(i, "floor_no", e.target.value)}
              />

              <button
                type="button"
                onClick={() => removeExhibitor(i)}
                className="rounded-xl bg-red-500 px-3 py-3 text-white"
              >
                ✕
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addExhibitor}
            className="rounded-xl bg-green-600 px-4 py-2 text-white"
          >
            + Add Exhibitor
          </button>
        </div>
      </Section>

      <div className="flex justify-between">
        {onBack && (
          <button type="button" onClick={onBack} className="rounded-xl bg-red-500 px-6 py-3 text-white">
            Back
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-brand-500 px-6 py-3 text-white"
        >
          {submitLabel || (initialData ? "Update Event" : "Create Event")}
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </section>
  );
}

function Input({ label, required, error, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        {...props}
        className={`w-full rounded-xl border p-3 ${error ? "border-red-500" : ""}`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Textarea({ label, required, error, ...props }) {
  return (
    <div className="md:col-span-2">
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        {...props}
        className={`w-full rounded-xl border p-3 ${error ? "border-red-500" : ""}`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function SelectInput({ label, required, error, children, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...props}
        className={`w-full rounded-xl border p-3 ${error ? "border-red-500" : ""}`}
      >
        {children}
      </select>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
